import React, { useEffect, useState } from "react";
import {
	Card,
	Col,
	Row,
	Typography,
	Button,
	Form,
	Input,
	Select,
	Switch,
	InputNumber,
	Space,
	message,
	Spin,
	Divider,
	Upload,
	Image,
	theme,
} from "antd";
import {
	SaveOutlined,
	ArrowLeftOutlined,
	PlusOutlined,
	DeleteOutlined,
	UploadOutlined,
} from "@ant-design/icons";
import { useNavigate, useParams } from "react-router";
import {
	requestGetBadgeDetail,
	requestCreateBadge,
	requestUpdateBadge,
} from "../../services/admin.service";

const { Title } = Typography;
const { Option } = Select;
const { TextArea } = Input;

const CATEGORY_OPTIONS = [
	"PRAYER",
	"QURAN",
	"DHIKR",
	"LEARNING",
	"CONSISTENCY",
	"MILESTONE",
	"SOCIAL",
	"SPECIAL",
];
const RARITY_OPTIONS = ["COMMON", "UNCOMMON", "RARE", "EPIC", "LEGENDARY"];
const REQUIREMENT_TYPE_OPTIONS = [
	"PRAYER_COUNT",
	"QURAN_VERSES",
	"DHIKR_COUNT",
	"STREAK_DAYS",
	"TOTAL_DURATION",
	"PERFECT_WEEK",
	"MILESTONE_ACHIEVEMENT",
	"SOCIAL_SHARING",
];
const TIMEFRAME_OPTIONS = ["DAILY", "WEEKLY", "MONTHLY", "YEARLY", "LIFETIME"];

function BadgeForm() {
	const { id } = useParams();
	const navigate = useNavigate();
	const [form] = Form.useForm();
	const { token } = theme.useToken();
	const isEditMode = Boolean(id);

	const [loading, setLoading] = useState(false);
	const [fetchingBadge, setFetchingBadge] = useState(false);
	const [iconFile, setIconFile] = useState(null);
	const [iconPreview, setIconPreview] = useState(null);
	const [existingIconId, setExistingIconId] = useState(null);
	const [existingIconUrl, setExistingIconUrl] = useState(null);

	useEffect(() => {
		document.title = isEditMode ? "Islamiva Console | Edit Badge" : "Islamiva Console | Add Badge";

		if (isEditMode) {
			loadBadge();
		}
	}, [id]);

	const loadBadge = () => {
		setFetchingBadge(true);
		requestGetBadgeDetail(id)
			.then((response) => {
				const badge = response?.data?.data;
				if (badge) {
					form.setFieldsValue({
						code: badge.code ?? badge.key,
						name: badge.name,
						description: badge.description,
						category: badge.category,
						rarity: badge.rarity,
						activated: badge.activated,
						order: badge.order,
						requirements: badge.requirements ?? [],
					});
					setExistingIconId(badge.iconId);
					setExistingIconUrl(badge.icon?.url ?? null);
				} else {
					message.error("Badge not found");
					navigate("/gamification/badges");
				}
			})
			.catch(() => {
				message.error("Failed to load badge data");
				navigate("/gamification/badges");
			})
			.finally(() => {
				setFetchingBadge(false);
			});
	};

	const handleIconBeforeUpload = (file) => {
		const isImage = file.type.startsWith("image/");
		if (!isImage) {
			message.error("Only image files are allowed");
			return Upload.LIST_IGNORE;
		}
		const url = URL.createObjectURL(file);
		setIconFile(file);
		setIconPreview(url);
		return false;
	};

	const handleIconRemove = () => {
		setIconFile(null);
		setIconPreview(null);
		setExistingIconUrl(null);
		setExistingIconId(null);
	};

	const handleSubmit = async (values) => {
		if (!iconFile && !existingIconId) {
			message.error("Please upload a badge icon");
			return;
		}
		setLoading(true);
		try {
			const formData = new FormData();
			Object.entries(values).forEach(([key, value]) => {
				if (key === "requirements") {
					if (value && value.length > 0) {
						formData.append(key, JSON.stringify(value));
					}
				} else if (value !== undefined && value !== null) {
					formData.append(key, value);
				}
			});
			if (iconFile) {
				formData.append("icon", iconFile);
			}

			const request = isEditMode
				? requestUpdateBadge(id, formData)
				: requestCreateBadge(formData);

			await request;
			message.success(isEditMode ? "Badge updated successfully" : "Badge created successfully");
			navigate("/gamification/badges");
		} catch (error) {
			const errMsg = error?.response?.data?.message || "An error occurred. Please try again.";
			message.error(errMsg);
		} finally {
			setLoading(false);
		}
	};

	return (
		<Spin spinning={fetchingBadge}>
			<Card
				title={
					<Space direction="horizontal" size="small">
						<Button icon={<ArrowLeftOutlined />} onClick={() => navigate(-1)} />
						<Divider type="vertical" />
            <Title level={4} style={{ margin: 0 }}>
							{isEditMode ? "Edit Badge" : "Add New Badge"}
						</Title>
					</Space>
				}>
				<Form
					form={form}
					layout="vertical"
					onFinish={handleSubmit}
					initialValues={{
						activated: true,
						order: 0,
						category: "SPECIAL",
						rarity: "COMMON",
						requirements: [],
					}}>
					{/* Icon Upload */}
					<Form.Item label="Icon">
						<Space align="start">
							<Upload
								accept="image/*"
								showUploadList={false}
								beforeUpload={handleIconBeforeUpload}
								onRemove={handleIconRemove}>
								<Button icon={<UploadOutlined />}>
									{iconFile || existingIconUrl ? "Change Icon" : "Upload Icon"}
								</Button>
							</Upload>
							{(iconPreview || existingIconUrl) && (
								<Space align="center">
									<Image
										src={iconPreview ?? existingIconUrl}
										width={56}
										height={56}
										style={{ objectFit: "contain", borderRadius: token.borderRadius }}
									/>
									<Button
										size="small"
										danger
										type="text"
										icon={<DeleteOutlined />}
										onClick={handleIconRemove}
									/>
								</Space>
							)}
						</Space>
					</Form.Item>

					<Row gutter={16}>
						<Col xs={24} sm={12}>
							<Form.Item
								label="Code"
								name="code"
								rules={[
									{ required: true, message: "Please enter the badge code" },
									{
										pattern: /^[A-Z0-9_]+$/,
										message: "Code must be uppercase letters, numbers, and underscores only",
									},
								]}>
								<Input placeholder="e.g. B001_THE_EARLY_FORERUNNERS" />
							</Form.Item>
						</Col>
						<Col xs={24} sm={12}>
							<Form.Item
								label="Name"
								name="name"
								rules={[{ required: true, message: "Please enter the badge name" }]}>
								<Input placeholder="e.g. Assabiqunal awwalun" />
							</Form.Item>
						</Col>
					</Row>

					<Form.Item label="Description" name="description">
						<TextArea rows={3} placeholder="Badge description..." />
					</Form.Item>

					<Row gutter={16}>
						<Col xs={24} sm={8}>
							<Form.Item
								label="Category"
								name="category"
								rules={[{ required: true, message: "Please select a category" }]}>
								<Select placeholder="Select category">
									{CATEGORY_OPTIONS.map((cat) => (
										<Option key={cat} value={cat}>
											{cat}
										</Option>
									))}
								</Select>
							</Form.Item>
						</Col>
						<Col xs={24} sm={8}>
							<Form.Item
								label="Rarity"
								name="rarity"
								rules={[{ required: true, message: "Please select a rarity" }]}>
								<Select placeholder="Select rarity">
									{RARITY_OPTIONS.map((rarity) => (
										<Option key={rarity} value={rarity}>
											{rarity}
										</Option>
									))}
								</Select>
							</Form.Item>
						</Col>
						<Col xs={24} sm={8}>
							<Form.Item label="Order" name="order">
								<InputNumber min={0} style={{ width: "100%" }} />
							</Form.Item>
						</Col>
					</Row>

					<Form.Item label="Active" name="activated" valuePropName="checked">
						<Switch />
					</Form.Item>

					{/* Requirements */}
					<Divider orientation="left">Requirements</Divider>
					<Form.List name="requirements">
						{(fields, { add, remove }) => (
							<>
								{fields.map(({ key, name, ...restField }) => (
									<Card
										key={key}
										size="small"
										style={{ marginBottom: 12, background: token.colorFillAlter }}
										extra={
											<Button
												type="text"
												danger
												size="small"
												icon={<DeleteOutlined />}
												onClick={() => remove(name)}
											/>
										}>
										<Row gutter={16}>
											<Col xs={24} sm={8}>
												<Form.Item
													{...restField}
													label="Type"
													name={[name, "type"]}
													rules={[{ required: true, message: "Required" }]}>
													<Select placeholder="Select type">
														{REQUIREMENT_TYPE_OPTIONS.map((t) => (
															<Option key={t} value={t}>
																{t}
															</Option>
														))}
													</Select>
												</Form.Item>
											</Col>
											<Col xs={24} sm={8}>
												<Form.Item
													{...restField}
													label="Target"
													name={[name, "target"]}
													rules={[{ required: true, message: "Required" }]}>
													<InputNumber min={0} style={{ width: "100%" }} placeholder="e.g. 100" />
												</Form.Item>
											</Col>
											<Col xs={24} sm={8}>
												<Form.Item
													{...restField}
													label="Timeframe"
													name={[name, "timeframe"]}
													initialValue="LIFETIME">
													<Select>
														{TIMEFRAME_OPTIONS.map((t) => (
															<Option key={t} value={t}>
																{t}
															</Option>
														))}
													</Select>
												</Form.Item>
											</Col>
										</Row>
									</Card>
								))}
								<Button
									type="dashed"
									onClick={() => add({ timeframe: "LIFETIME" })}
									icon={<PlusOutlined />}
									style={{ width: "100%", marginBottom: 16 }}>
									Add Requirement
								</Button>
							</>
						)}
					</Form.List>

					<Divider />

					<Row justify="end">
						<Space>
							<Button onClick={() => navigate("/badges")}>Cancel</Button>
							<Button type="primary" htmlType="submit" icon={<SaveOutlined />} loading={loading}>
								{isEditMode ? "Save Changes" : "Create Badge"}
							</Button>
						</Space>
					</Row>
				</Form>
			</Card>
		</Spin>
	);
}

export default BadgeForm;
